import { FeatureEntitlementName } from '@jupiterone/entitlement-types';
import {
  Badge,
  JupiterTheme,
  useABAC,
  utilUrl,
  ReportIssueDialog,
  useFeatureFlags,
  urls,
} from '@jupiterone/jupiter-web-toolkit';
import Menu, { MenuProps } from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Tooltip from '@material-ui/core/Tooltip';
import { createStyles, makeStyles } from '@material-ui/styles';
import SchoolRounded from '@material-ui/icons/SchoolRounded';
import React, { useState } from 'react';
import {
  HelpCircle as HelpIcon,
  Book as BookIcon,
  Send as RequestFeatureIcon,
} from 'react-feather';
import { useAnchorElState } from 'react-use-object-state';
import BugReportIcon from '@material-ui/icons/BugReport';
import ForumIcon from '@material-ui/icons/Forum';

type MenuItem = {
  Icon: (props: any) => JSX.Element;
  iconProps?: object;
  title: string;
  entitlement?: FeatureEntitlementName;
  onClick: VoidFunction;
  render: boolean;
};

const useStyles = makeStyles((theme: JupiterTheme) => {
  const NAV_ICON_SIZE = 28;
  const NAV_ICON_SIZE_SPACING = theme.spacing(2.5);
  return createStyles({
    settingsButton: {
      height: 28,
      '& svg': {
        width: NAV_ICON_SIZE,
        height: NAV_ICON_SIZE,
        marginLeft: NAV_ICON_SIZE_SPACING,
        cursor: 'pointer',
        '&.active': {
          color: theme.palette.primary.light,
        },
        '&:hover': {
          color: theme.palette.primary.light,
        },
        '&:focus': {
          outline: 'none',
        },
      },
    },
    menuItem: {
      justifyContent: 'flex-start',
      padding: 0,
    },
    subMenuItem: {
      justifyContent: 'flex-start',
      paddingRight: theme.spacing(3.25),
      paddingLeft: theme.spacing(3.25),
      '& a': {
        textDecoration: 'none',
        display: 'flex',
        color: 'inherit',
        '&:visited': {
          color: 'inherit',
        },
      },
      '& b': {
        color: theme.palette.coolGrey[400],
        transition: 'color 0.3s',
      },
      '&:hover': {
        backgroundColor: theme.palette.coolGrey[50],
        '& b': {
          color: theme.palette.primary.main,
        },
      },
    },
    iconContainer: {
      display: 'flex',
      alignItems: 'center',
      color: 'inherit',
    },
    menuIcon: {
      display: 'flex',
      color: theme.palette.primary.main,
      marginRight: theme.spacing(1.25),
      transition: 'color 0.3s',
      ':hover > &': {
        color: theme.palette.primary.main,
      },
    },
    settingsBadge: {
      position: 'absolute',
      right: '5px',
      bottom: '8px',
    },
  });
});

const ResourcesMenu = ({ ...props }: Omit<MenuProps, 'open'>) => {
  const classes = useStyles();
  const { isAdmin } = useABAC();
  const { hasFeature } = useFeatureFlags();
  const communityFeatureFlagActive = hasFeature('linksToTheCommunity');
  const [reportIssueModalOpen, setReportIssueModalOpen] = useState<boolean>(
    false
  );
  const handleClick = () => {
    utilUrl.navigateAbsoluteURL('/welcome/paths');
    closeMenu();
  };
  const { anchorEl, setAnchorEl, clearAnchorEl } = useAnchorElState(null);
  const recordButtonPosition = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    clearAnchorEl();
  };
  return (
    <>
      <div className={classes.settingsButton}>
        <Badge
          aria-haspopup="true"
          classes={{ badge: classes.settingsBadge }}
          overlap="circle"
          size="SMALL"
          anchorOrigin={{
            horizontal: 'right',
            vertical: 'bottom',
          }}
          onClick={setAnchorEl}
        >
          <Tooltip
            onClick={recordButtonPosition}
            title="Resources"
            enterDelay={750}
          >
            <div>
              {' '}
              <HelpIcon
                aria-label="Settings"
                aria-haspopup="true"
                className={anchorEl ? 'active' : ''}
              />
            </div>
          </Tooltip>
        </Badge>
      </div>
      <Menu
        data-testid="navbar-resources-menu"
        aria-label="Resources menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={clearAnchorEl}
        disableAutoFocusItem={true}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        {...props}
      >
        <MenuItem
          data-testid="navbar-resources-menu-documentation-link"
          className={classes.subMenuItem}
          onClick={closeMenu}
        >
          <a
            href={urls.documentation}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.iconContainer}
          >
            <BookIcon
              className={classes.menuIcon}
              tabIndex={0}
              aria-label="Documentation"
            />{' '}
            Documentation
          </a>
        </MenuItem>
        {isAdmin && (
          <MenuItem
            data-testid="navbar-resources-menu-onboarding-link"
            className={classes.subMenuItem}
            onClick={handleClick}
          >
            <SchoolRounded
              className={classes.menuIcon}
              tabIndex={1}
              aria-label="OnBoarding"
            />{' '}
            Onboarding
          </MenuItem>
        )}
        <MenuItem
          data-testid="navbar-resources-menu-onboarding-link"
          className={classes.subMenuItem}
          onClick={() => setReportIssueModalOpen(true)}
        >
          <BugReportIcon
            className={classes.menuIcon}
            tabIndex={2}
            aria-label="ReportIssue"
          />{' '}
          Report an issue
        </MenuItem>
        <MenuItem
          data-testid="navbar-resources-menu-feature-link"
          className={classes.subMenuItem}
          onClick={closeMenu}
        >
          <a
            href={urls.requestFeatureLink}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.iconContainer}
          >
            <RequestFeatureIcon
              className={classes.menuIcon}
              tabIndex={2}
              aria-label="RequestFeature"
            />{' '}
            Request a feature
          </a>
        </MenuItem>
        {communityFeatureFlagActive && (
          <MenuItem
            data-testid="navbar-resources-menu-community-link"
            className={classes.subMenuItem}
            onClick={closeMenu}
          >
            <a
              href={urls.communityLink}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.iconContainer}
            >
              <ForumIcon
                className={classes.menuIcon}
                tabIndex={2}
                aria-label="Community"
              />{' '}
              Community
            </a>
          </MenuItem>
        )}
      </Menu>
      <ReportIssueDialog
        open={reportIssueModalOpen}
        onClose={() => setReportIssueModalOpen(false)}
      />
    </>
  );
};

export default ResourcesMenu;
